/**
 * Device Detection Utilities
 *
 * Provides centralized device detection for layout mode and input mode.
 *
 * Key Concepts:
 * - Layout Mode: Determines UI structure (mobile/desktop) based on screen size + orientation
 * - Input Mode: Determines control type (touch/mouse) based on device capabilities
 *
 * These are SEPARATE concerns and should not be conflated.
 */

import {
  LAYOUT_MODE_MOBILE,
  LAYOUT_MODE_DESKTOP,
  INPUT_MODE_TOUCH,
  INPUT_MODE_MOUSE,
  ORIENTATION_PORTRAIT,
  ORIENTATION_LANDSCAPE,
  POINTER_COARSE,
  POINTER_FINE,
  type LayoutMode,
  type InputMode,
  type Orientation,
  type Pointer,
} from '../constants/deviceDetection';
import { MOBILE_MAX_WIDTH, TABLET_PORTRAIT_MAX_WIDTH } from '../constants/ui';

/**
 * Detects if the device has touch capability
 * Uses multiple detection methods for maximum compatibility
 */
export const hasTouch = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigator as any).msMaxTouchPoints > 0
  );
};

/**
 * Detects the primary pointer type (coarse = touch, fine = mouse)
 * Uses CSS media query for accurate detection
 */
export const getPrimaryPointer = (): Pointer => {
  if (typeof window === 'undefined') return POINTER_FINE;

  // Check pointer media query (most reliable method)
  if (window.matchMedia('(pointer: coarse)').matches) {
    return POINTER_COARSE; // Touch device
  }

  return POINTER_FINE; // Mouse device
};

/**
 * Detects if the device has hover capability
 * Touch devices typically don't have hover
 */
export const hasHover = (): boolean => {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(hover: hover)').matches;
};

/**
 * Gets the current device orientation
 */
export const getOrientation = (): Orientation => {
  if (typeof window === 'undefined') return ORIENTATION_LANDSCAPE;

  if (window.matchMedia('(orientation: portrait)').matches) {
    return ORIENTATION_PORTRAIT;
  }

  return ORIENTATION_LANDSCAPE;
};

/**
 * Determines the layout mode based on screen size and orientation
 *
 * Detection Strategy:
 * Uses the shorter dimension (min of width/height) to reliably detect phones vs tablets:
 *
 * PHONES (shorter dimension < 768px):
 * - Always mobile layout, regardless of orientation
 * - iPhone 14 Pro Max landscape: min(932, 430) = 430px → MOBILE ✅
 * - Galaxy S23 Ultra landscape: min(915, 412) = 412px → MOBILE ✅
 *
 * TABLETS (shorter dimension >= 768px):
 * - Portrait: Mobile layout (iPad Mini: 768px, iPad Air: 820px)
 * - Landscape: Desktop layout (iPad Pro 11": 834px landscape)
 *
 * DESKTOP:
 * - Always desktop layout
 *
 * This approach fixes the bug where phones in landscape orientation
 * were incorrectly detected as desktop due to width > 768px.
 */
export const getLayoutMode = (): LayoutMode => {
  if (typeof window === 'undefined') return LAYOUT_MODE_DESKTOP;

  const width = window.innerWidth;
  const height = window.innerHeight;

  // PHONES: Always mobile if shorter dimension <= MOBILE_MAX_WIDTH (both orientations)
  // This catches all phones regardless of orientation (e.g. iPhone landscape width > 1100px)
  const shortDimension = Math.min(width, height);
  if (shortDimension <= MOBILE_MAX_WIDTH) {
    return LAYOUT_MODE_MOBILE;
  }

  // Any window ≤ 1100px gets mobile layout (tablets, small desktops, etc.)
  if (width <= TABLET_PORTRAIT_MAX_WIDTH) {
    return LAYOUT_MODE_MOBILE;
  }

  // DESKTOP: width > 1100px
  return LAYOUT_MODE_DESKTOP;
};

/**
 * Determines the input mode based on device capabilities
 * Touch devices should use touch controls, mouse devices use mouse controls
 */
export const getInputMode = (): InputMode => {
  const pointer = getPrimaryPointer();
  return pointer === POINTER_COARSE ? INPUT_MODE_TOUCH : INPUT_MODE_MOUSE;
};

/**
 * Device state interface
 */
export interface DeviceState {
  /** Screen width in pixels */
  width: number;
  /** Screen height in pixels */
  height: number;
  /** Current orientation */
  orientation: Orientation;
  /** Whether device has touch capability */
  hasTouch: boolean;
  /** Primary pointer type */
  primaryPointer: Pointer;
  /** Whether device has hover capability */
  hasHover: boolean;
  /** Current layout mode */
  layoutMode: LayoutMode;
  /** Current input mode */
  inputMode: InputMode;
}

/**
 * Gets the current device state
 * Useful for non-reactive context (like Zustand store)
 */
export const getDeviceState = (): DeviceState => {
  if (typeof window === 'undefined') {
    return {
      width: 1920,
      height: 1080,
      orientation: ORIENTATION_LANDSCAPE,
      hasTouch: false,
      primaryPointer: POINTER_FINE,
      hasHover: true,
      layoutMode: LAYOUT_MODE_DESKTOP,
      inputMode: INPUT_MODE_MOUSE,
    };
  }

  const width = window.innerWidth;
  const height = window.innerHeight;
  const orientation = getOrientation();
  const touchCapability = hasTouch();
  const primaryPointer = getPrimaryPointer();
  const hoverCapability = hasHover();
  const layoutMode = getLayoutMode();
  const inputMode = getInputMode();

  return {
    width,
    height,
    orientation,
    hasTouch: touchCapability,
    primaryPointer,
    hasHover: hoverCapability,
    layoutMode,
    inputMode,
  };
};

// ============================================================================
// Helper Functions - Single Source of Truth for Mode Comparisons
// ============================================================================

/**
 * Checks if given layout mode is mobile
 * @param layoutMode - The layout mode to check
 * @returns true if mobile layout
 */
export const isMobileLayout = (layoutMode: LayoutMode): boolean => {
  return layoutMode === LAYOUT_MODE_MOBILE;
};

/**
 * Checks if given layout mode is desktop
 * @param layoutMode - The layout mode to check
 * @returns true if desktop layout
 */
export const isDesktopLayout = (layoutMode: LayoutMode): boolean => {
  return layoutMode === LAYOUT_MODE_DESKTOP;
};

/**
 * Checks if given input mode is touch
 * @param inputMode - The input mode to check
 * @returns true if touch input
 */
export const isTouchInput = (inputMode: InputMode): boolean => {
  return inputMode === INPUT_MODE_TOUCH;
};

/**
 * Checks if given input mode is mouse
 * @param inputMode - The input mode to check
 * @returns true if mouse input
 */
export const isMouseInput = (inputMode: InputMode): boolean => {
  return inputMode === INPUT_MODE_MOUSE;
};

/**
 * Checks if given orientation is portrait
 * @param orientation - The orientation to check
 * @returns true if portrait orientation
 */
export const isPortraitOrientation = (orientation: Orientation): boolean => {
  return orientation === ORIENTATION_PORTRAIT;
};

/**
 * Checks if given orientation is landscape
 * @param orientation - The orientation to check
 * @returns true if landscape orientation
 */
export const isLandscapeOrientation = (orientation: Orientation): boolean => {
  return orientation === ORIENTATION_LANDSCAPE;
};
